/**
 * Pre-generation pickers shown above the composer when the draft looks like a media ask:
 *  - image / video  → a DIMENSION picker (4 social-platform aspect ratios, with tooltips)
 *  - music          → a SINGER panel (voice + voice style + genre → a `musicStyle` descriptor)
 *
 * Purely presentational: the parent owns the selected state and threads it into onSend as
 * `mediaAspect` / `musicStyle`. Monochrome, Lucide-only, in keeping with the design rules.
 */
import React from 'react';
import {
  Ratio, Maximize2, Music,
  ZoomIn, ZoomOut, Orbit, MoveUp, Expand, Plane, MoveHorizontal, Focus, Hand,
  type LucideIcon,
} from 'lucide-react';
import {
  ASPECT_OPTIONS, SIZE_OPTIONS, SINGER_VOICES, VOICE_STYLES, MUSIC_GENRES, STYLE_PRESETS, CAMERA_MOVES,
  SONG_LENGTHS, DEFAULT_SONG_LENGTH, formatSongLength, MUSIC_KINDS, DEFAULT_MUSIC_KIND,
  type MediaIntent, type MusicChoice,
} from '../../utils/mediaIntent';
import { TYPE } from '../../constants/typeScale';

// Teach each camera move — a directional glyph + a plain-language tooltip, so film jargon (rack focus,
// whip pan) is legible to a non-filmmaker (the Higgsfield "show, don't name" lesson, in our chip idiom).
const MOVE_META: Record<string, { Icon: LucideIcon; hint: string }> = {
  push_in: { Icon: ZoomIn, hint: 'Camera moves toward the subject' },
  pull_out: { Icon: ZoomOut, hint: 'Camera pulls back to reveal the scene' },
  orbit: { Icon: Orbit, hint: 'Camera circles around the subject' },
  crane_up: { Icon: MoveUp, hint: 'Camera rises up and over' },
  crash_zoom: { Icon: Expand, hint: 'Fast zoom punch-in' },
  fpv_drone: { Icon: Plane, hint: 'Fast, immersive fly-through' },
  whip_pan: { Icon: MoveHorizontal, hint: 'Fast sideways swish' },
  rack_focus: { Icon: Focus, hint: 'Focus shifts between near and far' },
  handheld: { Icon: Hand, hint: 'Natural handheld motion' },
};

interface Props {
  intent: MediaIntent;
  aspect: string;
  onAspect: (value: string) => void;
  music: MusicChoice;
  onMusic: (next: MusicChoice) => void;
  /** Selected style-preset key (prompt factory), '' = none. */
  style: string;
  onStyle: (value: string) => void;
  /** Selected camera-move key (video only), '' = none. */
  cameraMove: string;
  onCameraMove: (value: string) => void;
  /** Output size (resolution): 'standard' | 'hd'. */
  size: string;
  onSize: (value: string) => void;
}

const chip = (active: boolean) =>
  `px-2.5 py-1.5 rounded-full ${TYPE.control} border transition-colors ${
    active
      ? 'bg-secondary-900 text-white border-secondary-900 dark:bg-white dark:text-secondary-900 dark:border-white'
      : 'bg-transparent text-secondary-600 border-secondary-200 hover:border-secondary-400 dark:text-secondary-300 dark:border-secondary-600'
  }`;

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-center gap-2 flex-wrap">
    <span className={`${TYPE.label} text-secondary-400 w-16 shrink-0`}>{label}</span>
    <div className="flex gap-1.5 flex-wrap">{children}</div>
  </div>
);

const GenerationOptions: React.FC<Props> = ({ intent, aspect, onAspect, music, onMusic, style, onStyle, cameraMove, onCameraMove, size, onSize }) => {
  if (intent === 'image' || intent === 'video') {
    const styles = STYLE_PRESETS[intent];
    return (
      <div className="px-3 pt-2 pb-1 space-y-1.5" data-testid="dimension-picker">
        {/* Prompt factory — pick a look without typing (toggle off if already selected). */}
        <Row label="Style">
          {styles.map((s) => (
            <button key={s.key} type="button" onClick={() => onStyle(style === s.key ? '' : s.key)} className={chip(style === s.key)} data-testid={`style-${s.key}`}>{s.label}</button>
          ))}
        </Row>
        {/* Camera move — VIDEO only. Named cinematographer moves (click one, not keyframes). Each carries a
            directional glyph + a plain tooltip so the film term is legible to a non-filmmaker. */}
        {intent === 'video' && (
          <Row label="Motion">
            {CAMERA_MOVES.map((m) => {
              const MIcon = MOVE_META[m.key]?.Icon;
              return (
                <button key={m.key} type="button" title={MOVE_META[m.key]?.hint}
                  onClick={() => onCameraMove(cameraMove === m.key ? '' : m.key)}
                  className={`${chip(cameraMove === m.key)} inline-flex items-center gap-1`} data-testid={`move-${m.key}`}>
                  {MIcon && <MIcon className="w-3.5 h-3.5" />}{m.label}
                </button>
              );
            })}
          </Row>
        )}
        {/* Dimension = aspect ratio (social-platform shape). */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1 ${TYPE.label} text-secondary-400 w-16 shrink-0`}>
            <Ratio className="w-3.5 h-3.5" /> Shape
          </span>
          <div className="flex gap-1.5 flex-wrap">
            {ASPECT_OPTIONS.map((o) => (
              <button key={o.value} type="button" title={`${o.value} — ${o.platforms}`} onClick={() => onAspect(o.value)} className={chip(aspect === o.value)}>
                {o.label} <span className="opacity-60">{o.value}</span>
              </button>
            ))}
          </div>
        </div>
        {/* Size = output resolution/quality. */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1 ${TYPE.label} text-secondary-400 w-16 shrink-0`}>
            <Maximize2 className="w-3.5 h-3.5" /> Size
          </span>
          <div className="flex gap-1.5 flex-wrap">
            {SIZE_OPTIONS.map((o) => (
              <button key={o.value} type="button" title={o.hint} onClick={() => onSize(o.value)} className={chip(size === o.value)} data-testid={`size-${o.value}`}>{o.label}</button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (intent === 'music') {
    const kind = music.kind || DEFAULT_MUSIC_KIND;
    const isSfx = kind === 'sfx';
    const isVocal = kind === 'music';
    return (
      <div className="px-3 pt-2 pb-1 space-y-1.5" data-testid="singer-panel">
        <span className={`inline-flex items-center gap-1 ${TYPE.label} text-secondary-400`}>
          <Music className="w-3.5 h-3.5" /> Audio
        </span>
        {/* Type routes to the best model behind an abstract capability (Song / Instrumental / Sound FX). */}
        <Row label="Type">
          {MUSIC_KINDS.map((k) => (
            <button key={k.key} type="button" title={k.hint} onClick={() => onMusic({ ...music, kind: k.key })}
              className={chip(kind === k.key)} data-testid={`music-kind-${k.key}`}>{k.label}</button>
          ))}
        </Row>
        {isVocal && (
          <Row label="Voice">
            {SINGER_VOICES.map((v) => (
              <button key={v} type="button" onClick={() => onMusic({ ...music, voice: v })} className={chip(music.voice === v)}>{v}</button>
            ))}
          </Row>
        )}
        {isVocal && (
          <Row label="Style">
            {VOICE_STYLES.map((v) => (
              <button key={v} type="button" onClick={() => onMusic({ ...music, voiceStyle: v })} className={chip(music.voiceStyle === v)}>{v}</button>
            ))}
          </Row>
        )}
        {!isSfx && (
          <Row label="Genre">
            {MUSIC_GENRES.map((g) => (
              <button key={g} type="button" onClick={() => onMusic({ ...music, genre: g })} className={chip(music.genre === g)}>{g}</button>
            ))}
          </Row>
        )}
        {/* Length — longer tracks cost proportionally more (billed per 30 seconds). SFX is a fixed short clip. */}
        {!isSfx && (
          <Row label="Length">
            {SONG_LENGTHS.map((s) => (
              <button key={s} type="button" onClick={() => onMusic({ ...music, durationSec: s })}
                title={formatSongLength(s)}
                className={chip((music.durationSec || DEFAULT_SONG_LENGTH) === s)}>{formatSongLength(s)}</button>
            ))}
          </Row>
        )}
      </div>
    );
  }

  return null;
};

export default GenerationOptions;
